import * as React from "react";

import Form from "@ant-design/compatible/es/form";
import { AjaxManager } from "@impero-com/common/utils/ajax";
import { t } from "@impero-com/common/utils/i18n";
import { assertNever, clone, set } from "@impero-com/common/utils/obj";
import { Tooltip } from "@impero-com/ui";
import type { CheckboxChangeEvent } from "antd/es/checkbox";

import {
    ControlPolicy,
    UpdatableControls,
    Presence,
    ControlProgramOverview,
    UserTagOverviewListDto,
} from "api/generated/model";
import { MultiCheckboxInput } from "components/form/legacyFields/multiCheckboxInput";
import { MultiControlProgramTypeahead } from "components/form/legacyFields/multiControlProgramTypeahead";
import { MultiStringInput } from "components/form/legacyFields/multiStringInput";
import { MultiTextArea } from "components/form/legacyFields/multiTextArea";
import * as formTheme from "components/form/legacyFields/theme.css";
import { TagsSelector, TagsSelectorValues } from "components/tag/selector";
import { FormRootComponentProps } from "utils/form";
import { Guid } from "utils/guid";

import * as theme from "./theme.css";

interface ChangeDetailsFormValues {
    controlNames: Array<string>;
    controlPrograms: Array<ControlProgramOverview>;
    descriptions: Array<string>;
    tags: TagsSelectorValues;
    loginRequired: Array<boolean>;
    patch: UpdatableControls;
}

interface ChangeDetailsFormProps extends FormRootComponentProps<ChangeDetailsFormValues> {
    ajaxManager: AjaxManager;
    readOnly: boolean;
    allTags: UserTagOverviewListDto;
    controlIds: Array<Guid>;
    initialControlNames: Array<string>;
    initialControlPrograms: Array<ControlProgramOverview>;
    initialDescriptions: Array<string>;
    initialTags: Array<Set<Guid>>;
    initialLoginRequired: Array<boolean>;
    loginProtectionPolicy: ControlPolicy;
}

interface LoginRequiredState {
    values: Array<boolean>;
    enforced: boolean;
}

function getLoginRequiredState(
    policy: ControlPolicy,
    initialLoginRequired: Array<boolean>
): LoginRequiredState {
    switch (policy.mode) {
        case "Default":
            return { values: initialLoginRequired, enforced: false };
        case "Enforced":
            return {
                values: initialLoginRequired.map(() => true),
                enforced: true,
            };
        default:
            return assertNever(policy);
    }
}

class ChangeDetailsForm extends React.PureComponent<ChangeDetailsFormProps> {
    render = () => {
        const { form, controlIds } = this.props;
        return (
            <>
                {this.renderControlNames()}
                {this.renderControlPrograms()}
                {this.renderDescriptions()}
                {this.renderTags()}
                {this.renderCheckboxes()}
                {
                    // This is only used to set the initial patch value.
                    // It ensures it is always defined.
                    form.getFieldDecorator("patch", {
                        trigger: "", // Update handled by calls to setFieldsValue
                        initialValue: { controlIds },
                    })(<></>)
                }
            </>
        );
    };

    private renderControlNames = () => {
        const { form, initialControlNames, readOnly } = this.props;
        return form.getFieldDecorator("controlNames", {
            trigger: "", // Update handled by calls to setFieldsValues
            valuePropName: "values",
            initialValue: initialControlNames,
        })(
            !readOnly ? (
                <MultiStringInput
                    type="CREATE"
                    name="name"
                    label={t("Control_Title")}
                    onChange={(newName: string) => {
                        form.setFieldsValue({
                            controlNames: Array<string>(
                                initialControlNames.length
                            ).fill(newName),
                            patch: set(form.getFieldValue("patch"), {
                                title: newName,
                            }),
                        });
                    }}
                />
            ) : (
                <MultiStringInput
                    type="READ_ONLY"
                    name="name"
                    label={t("Control_Title")}
                />
            )
        );
    };

    private renderControlPrograms = () => {
        const { form, initialControlPrograms, readOnly, ajaxManager } =
            this.props;
        return form.getFieldDecorator("controlPrograms", {
            trigger: "", // Update handled by calls to setFieldsValues
            valuePropName: "values",
            initialValue: initialControlPrograms,
        })(
            !readOnly ? (
                <MultiControlProgramTypeahead
                    type="CREATE"
                    name="controlProgram"
                    label={t("Control_ControlProgram")}
                    onSelect={(newCp: ControlProgramOverview | undefined) => {
                        if (newCp !== undefined) {
                            form.setFieldsValue({
                                controlPrograms: Array<ControlProgramOverview>(
                                    initialControlPrograms.length
                                ).fill(newCp),
                                patch: set(form.getFieldValue("patch"), {
                                    controlProgramId: newCp.id,
                                }),
                            });
                        }
                    }}
                    ajaxManager={ajaxManager}
                />
            ) : (
                <MultiControlProgramTypeahead
                    type="READ_ONLY"
                    name="controlProgram"
                    label={t("Control_ControlProgram")}
                />
            )
        );
    };

    private renderDescriptions = () => {
        const { form, initialDescriptions, readOnly } = this.props;
        return form.getFieldDecorator("descriptions", {
            trigger: "", // Update handled by calls to setFieldsValues
            valuePropName: "values",
            initialValue: initialDescriptions,
        })(
            !readOnly ? (
                <MultiTextArea
                    type="CREATE"
                    name="description"
                    label={t("Control_Description")}
                    onChange={(newDesc: string) => {
                        form.setFieldsValue({
                            descriptions: Array<string>(
                                initialDescriptions.length
                            ).fill(newDesc),
                            patch: set(form.getFieldValue("patch"), {
                                description: newDesc,
                            }),
                        });
                    }}
                />
            ) : (
                <MultiTextArea
                    type="READ_ONLY"
                    name="description"
                    label={t("Control_Description")}
                />
            )
        );
    };

    private renderTags = () => {
        const { form, initialTags, readOnly, allTags } = this.props;
        const tagsContent = allTags.content.map((t) => t.resource);
        return form.getFieldDecorator("tags", {
            trigger: "", // Update handled by calls to setFieldsValues
            valuePropName: "values",
            initialValue: {
                tags: initialTags,
                absentTags: new Set(),
            },
        })(
            !readOnly ? (
                <TagsSelector
                    type="CREATE"
                    label={t("Control_Tags")}
                    helpText={t("Control_Details_Tags_Help")}
                    allTagCategories={allTags.linkedResources}
                    allTags={tagsContent}
                    onAddTag={this.handleAddTag}
                    onRemoveTag={this.handleRemoveTag}
                />
            ) : (
                <TagsSelector
                    type="READ_ONLY"
                    label={t("Control_Tags")}
                    helpText={t("Control_Details_Tags_Help")}
                    allTagCategories={allTags.linkedResources}
                    allTags={tagsContent}
                />
            )
        );
    };

    private renderCheckboxes = () => {
        /* Yes, it is weird that we require Form.Item here, but MultiCheckboxInput's API is not aligned with the rest of our "forminput" components.
        This probably needs a cleanup at some point.
        -- opi */
        const { form, initialLoginRequired, loginProtectionPolicy, readOnly } =
            this.props;
        const loginRequired = getLoginRequiredState(
            loginProtectionPolicy,
            initialLoginRequired
        );
        return (
            <div className={theme.checkboxesContainer}>
                <Tooltip
                    title={t("Control_Policy_EnforcedTooltip")}
                    disabled={!loginRequired.enforced}
                >
                    <Form.Item className={formTheme.imperoFormField}>
                        {form.getFieldDecorator("loginRequired", {
                            trigger: "", // Update handled by calls to setFieldsValues
                            valuePropName: "values",
                            initialValue: loginRequired.values,
                        })(
                            !readOnly ? (
                                <MultiCheckboxInput
                                    type="CREATE"
                                    label={t("Control_LoginRequired")}
                                    disabled={loginRequired.enforced}
                                    onCheckAllChange={(
                                        e: CheckboxChangeEvent
                                    ) => {
                                        const required = e.target.checked;
                                        form.setFieldsValue({
                                            loginRequired: Array<boolean>(
                                                initialLoginRequired.length
                                            ).fill(required),
                                            patch: set(
                                                form.getFieldValue("patch"),
                                                {
                                                    loginProtectionEnabled:
                                                        required,
                                                }
                                            ),
                                        });
                                    }}
                                />
                            ) : (
                                <MultiCheckboxInput
                                    type="READ_ONLY"
                                    label={t("Control_LoginRequired")}
                                />
                            )
                        )}
                    </Form.Item>
                </Tooltip>
            </div>
        );
    };

    private handleAddTag = (tagId: Guid) => {
        const { form, controlIds } = this.props;
        const values = form.getFieldValue("tags") || { tags: [] };
        const patch: UpdatableControls = form.getFieldValue("patch") || {
            controlIds,
        };
        const newTags = values.tags.map((tagIds) => {
            const newTagIds = new Set(tagIds);
            newTagIds.add(tagId);
            return newTagIds;
        });
        const absentTags = values.absentTags || [];
        const newAbsentTags = new Set(absentTags);
        newAbsentTags.delete(tagId);
        const newPatch: Record<string, Presence> = patch.tagIds
            ? clone(patch.tagIds)
            : {};
        newPatch[tagId] = "Present";
        form.setFieldsValue({
            tags: {
                tags: newTags,
                absentTags: newAbsentTags,
            },
            patch: set(patch, { tagIds: newPatch }),
        });
    };

    private handleRemoveTag = (tagId: Guid) => {
        const { form, controlIds } = this.props;
        const values = form.getFieldValue("tags") || { tags: [] };
        const patch = form.getFieldValue("patch") || { controlIds };
        const newTags = values.tags.map((tagIds) => {
            const newTagIds = new Set(tagIds);
            newTagIds.delete(tagId);
            return newTagIds;
        });
        const absentTags = values.absentTags || [];
        const newAbsentTags = new Set(absentTags);
        newAbsentTags.add(tagId);
        const newPatch: Record<string, Presence> = patch.tagIds
            ? clone(patch.tagIds)
            : {};
        newPatch[tagId] = "Absent";
        form.setFieldsValue({
            tags: {
                tags: newTags,
                absentTags: newAbsentTags,
            },
            patch: set(patch, { tagIds: newPatch }),
        });
    };
}

export { type ChangeDetailsFormValues, ChangeDetailsForm };
